/** Mirrors `EPN.Dashboard.Api`'s `DashboardSummaryResponse` field-for-field. `trend` is `null` whenever the
 * prior period had no comparable data — render NO badge in that case (never default it to green or red).
 * See docs/dashboard-architecture.md for the exact legacy-bug fixes this null represents. */
export interface TrendInfo {
  isUp: boolean;
  percentChange: number;
}

/** `amount` is `null` when the figure could not be computed (a downstream module was unreachable). A null is
 * never a zero: render "Data unavailable" for it. */
export interface MoneyStatCard {
  amount: number | null;
  trend: TrendInfo | null;
}

export interface CountStatCard {
  count: number | null;
  trend: TrendInfo | null;
}

/** The period the figures cover and the one they are compared with: calendar months in the company's time zone.
 * `timeZoneId` is the zone the month boundaries were computed in ("UTC" until the company chooses one). */
export interface DashboardPeriod {
  label: string;
  start: string;
  end: string;
  comparisonLabel: string;
  comparisonStart: string;
  comparisonEnd: string;
  timeZoneId: string;
}

/** Where the period's money went. `grossWages = netPay + employeeTaxes + employeeDeductions` and
 * `total = grossWages + employerTaxes`. Employee withholdings sit INSIDE gross wages and are never added to the
 * total a second time. Employer-paid benefits and payroll service fees are not part of `total` (no authoritative
 * per-run source exists yet). */
export interface PayrollBreakdown {
  total: number;
  grossWages: number;
  employerTaxes: number;
  employeeTaxes: number;
  employeeDeductions: number;
  netPay: number;
}

/** Proof the figures agree. `difference` should be exactly 0; a non-zero value is shown, never hidden. */
export interface Reconciliation {
  grossWages: number;
  netPlusWithholdings: number;
  difference: number;
  isReconciled: boolean;
}

export interface DeductionCategory {
  name: string;
  amount: number;
}

export interface DeductionsBreakdown {
  total: number;
  categories: DeductionCategory[];
}

export interface TaxAlert {
  formLabel: string;
  amount: number;
  dueDate: string;
  isOverdue: boolean;
}

export interface AnnouncementSummary {
  id: string;
  title: string;
  body: string;
  publishedAtUtc: string;
}

export interface DashboardSectionAvailability {
  payrollUnavailable: boolean;
  taxesUnavailable: boolean;
  deductionsUnavailable: boolean;
}

export interface DashboardSummaryResponse {
  generatedAtUtc: string;
  period: DashboardPeriod;
  hasAnyEmployees: boolean;
  totalEmployerPayrollCost: MoneyStatCard;
  totalTaxesDue: MoneyStatCard;
  totalEmployeesPaid: CountStatCard;
  netPay: MoneyStatCard;
  /** Null when the payroll section is unavailable. */
  payrollBreakdown: PayrollBreakdown | null;
  /** Null when the payroll section is unavailable. */
  reconciliation: Reconciliation | null;
  deductionsBreakdown: DeductionsBreakdown;
  alerts: TaxAlert[];
  announcements: AnnouncementSummary[];
  availability: DashboardSectionAvailability;
}

/** One entry of the centralized metric dictionary (`GET /api/dashboard-metrics`). The page's tooltips render these,
 * so the definition a user reads is the same text the formula is documented with. */
export interface MetricDefinition {
  metricId: string;
  displayName: string;
  definition: string;
  formula: string;
  sourceModule: string;
  included: string;
  excluded: string;
  dateBasis: string;
  comparisonRule: string;
  requiredPermission: string;
  tenantScope: string;
  freshness: string;
  unavailableBehavior: string;
  availability: 'Supported' | 'Unavailable';
  unavailableReason: string | null;
}

/** `formatCurrency(9290.68) === "$9,290.68"` — matches the reference screenshot's exact formatting
 * (always 2 decimal places, thousands separator, no rounding to whole dollars). */
export function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** `formatPercent(51.4) === "51%"` — the reference screenshot shows whole-number percentages only. */
export function formatPercent(percent: number): string {
  return `${Math.round(percent)}%`;
}

/** The reference screenshot's official Product Offerings & Other Services list — verbatim from legacy's
 * own `AdminDashboard.cshtml` (100% static markup there too, confirmed during investigation; no CMS or API
 * backs this list in either codebase). All ten cards, including the six not visible in the reference crop —
 * "Locate and preserve any additional partially visible service cards," per the assignment. */
export interface ProductOffering {
  title: string;
  description: string;
  iconAsset: string;
  learnMoreUrl: string;
}

export const PRODUCT_OFFERINGS: ProductOffering[] = [
  { title: 'Financial Intelligence', description: 'Review and optimize your business financial strategies.', iconAsset: '/img/offerings/Overview.svg', learnMoreUrl: 'https://www.escochecks.com/service-financial-intelligence' },
  { title: "Workers' Compensation", description: "Review worker's comp setup and classification needs.", iconAsset: '/img/offerings/Comp.svg', learnMoreUrl: 'https://www.escochecks.com/service-workers-comp' },
  { title: 'Medicare Cost Reports', description: 'Manage compliance and reporting for Medicare cost tracking.', iconAsset: '/img/offerings/ContRep.svg', learnMoreUrl: 'https://www.escochecks.com/service-medicare-cost' },
  { title: 'Escochecks Insurance Agency', description: 'Review business and employee insurance options.', iconAsset: '/img/offerings/Payment.svg', learnMoreUrl: 'https://www.escochecksinsurance.com/' },
  { title: 'Secretary of State Business Registration', description: 'Simplify corporate compliance and state filings.', iconAsset: '/img/offerings/Key.svg', learnMoreUrl: 'https://www.escochecks.com/service-sos-business-registration' },
  { title: 'Benefits Administration', description: 'Manage or explore employee benefits support.', iconAsset: '/img/offerings/Emp.svg', learnMoreUrl: 'https://www.escochecks.com/service-benefits-administration' },
  { title: 'Tax Preparation', description: 'Fast, accurate filing for business and federal taxes.', iconAsset: '/img/offerings/Taxes.svg', learnMoreUrl: 'https://www.escochecks.com/service-tax-preparation' },
  { title: 'Tax Resolution', description: 'Resolve past tax liabilities and IRS disputes.', iconAsset: '/img/offerings/TaxRep.svg', learnMoreUrl: 'https://www.escochecks.com/service-tax-resolution' },
  { title: 'Outsourced CFO', description: 'Expert financial leadership and strategic planning.', iconAsset: '/img/offerings/Payroll.svg', learnMoreUrl: 'https://www.escochecks.com/service-outsourced-cfo' },
  { title: 'Other Services', description: 'Discover other tailored solutions for your business.', iconAsset: '/img/offerings/ImpExp.svg', learnMoreUrl: 'https://www.escochecks.com/service-other-services' },
];

/** Mirrors `EPN.Dashboard.Api`'s `SuperAdminDashboardResponse` field-for-field (EPN-44). Unlike
 * `DashboardSummaryResponse` above, this is NOT scoped to one company — `companies` genuinely spans every
 * company on the platform, returned only to a caller holding `platform.companies.read` (SuperAdmin).
 * `payrollUnavailable`/`taxesUnavailable`/`securityUnavailable` are always `true` today: no cross-company
 * payroll/tax/audit aggregation exists anywhere in EPN (every other module's data is scoped to one company
 * via the caller's own JWT) — see docs/epn-44-super-admin-dashboard-gaps.md. Never rendered as a fabricated
 * zero or omitted silently; always shown as an honest "not available" state. */
export interface PlatformCompanyRow {
  id: string;
  name: string;
  isActive: boolean;
  createdAtUtc: string;
  memberCount: number;
}

export interface SuperAdminDashboardResponse {
  totalCompanies: number;
  activeCompanies: number;
  inactiveCompanies: number;
  companies: PlatformCompanyRow[];
  companiesUnavailable: boolean;
  payrollUnavailable: boolean;
  taxesUnavailable: boolean;
  securityUnavailable: boolean;
}

/** Blue palette — Payroll Breakdown donut, verbatim from legacy's `DashboardScript.js`. */
export const PAYROLL_BREAKDOWN_COLORS = {
  netPay: '#bee2ff',
  employeeTaxes: '#1577bd',
  employeeDeductions: '#66abe9',
  employerTaxes: '#0e588d',
} as const;

/** Yellow/gold palette — Deductions Breakdown donut, verbatim from legacy's `DashboardScript.js`, cycled
 * if there are more than 5 legend rows (there never are — top 4 + "Others" caps it at 5). */
export const DEDUCTIONS_BREAKDOWN_COLORS = ['#ffeec6', '#febf2d', '#e6a100', '#ffcd5a', '#bf8500'];

/** ---- Executive dashboard (`GET /api/executive-dashboard`) ---- Mirrors `EPN.Dashboard.Api`'s `ExecutiveDashboardResponse`. A null section means that
 * section is unavailable (a downstream module could not be reached): render "Data unavailable", never a zero. */
export type ExecutivePeriodKind = 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'yearToDate' | 'trailing12Months' | 'custom';

export interface ExecutiveQuery {
  period: ExecutivePeriodKind;
  /** Only for `custom`: ISO dates (YYYY-MM-DD). */
  from?: string | undefined;
  to?: string | undefined;
}

export interface ExecutivePeriod {
  kind: ExecutivePeriodKind;
  label: string;
  start: string;
  end: string;
  comparisonLabel: string;
  comparisonStart: string;
  comparisonEnd: string;
  timeZoneId: string;
}

/** `grossWages = netPay + employeeTaxes + employeeDeductions` and `totalEmployerCost = grossWages + employerTaxes`. */
export interface ExecutiveCostTotals {
  grossWages: number;
  employerTaxes: number;
  employeeTaxes: number;
  employeeDeductions: number;
  netPay: number;
  totalEmployerCost: number;
  employeesPaid: number;
  /** Null when nobody was paid. */
  averageEmployerCostPerPaidEmployee: number | null;
  /** Gross wages minus (net pay + employee taxes + employee deductions). Should be exactly 0; a non-zero value is shown, never hidden. */
  reconciliationDifference: number;
}

/** `percent` is null when the comparison figure is zero. */
export interface Variance {
  amount: number;
  percent: number | null;
  isIncrease: boolean;
}

export interface ExecutiveTrendPoint {
  monthStart: string;
  label: string;
  grossWages: number;
  employerTaxes: number;
  totalEmployerCost: number;
  employeesPaid: number;
}

export interface PayrollStatusCount {
  status: string;
  label: string;
  count: number;
  actionPath: string | null;
}

/** A RETURN FILING date. Never a deposit deadline. */
export interface Obligation {
  kind: string;
  title: string;
  date: string;
  note: string;
  actionPath: string;
}

export type AlertSeverity = 'critical' | 'attention' | 'information';

export interface ExecutiveAlert {
  severity: AlertSeverity;
  title: string;
  detail: string;
  actionPath: string;
}

export interface UnavailableItem {
  id: string;
  label: string;
  reason: string;
}

export interface ExecutiveAvailability {
  payrollUnavailable: boolean;
  runsUnavailable: boolean;
  obligationsUnavailable: boolean;
  taxesUnavailable: boolean;
}

export interface ExecutiveDashboardResponse {
  generatedAtUtc: string;
  period: ExecutivePeriod;
  current: ExecutiveCostTotals | null;
  comparison: ExecutiveCostTotals | null;
  totalCostVariance: Variance | null;
  grossWagesVariance: Variance | null;
  trend: ExecutiveTrendPoint[] | null;
  payrollStatuses: PayrollStatusCount[] | null;
  obligations: Obligation[] | null;
  alerts: ExecutiveAlert[];
  unavailable: UnavailableItem[];
  availability: ExecutiveAvailability;
}
